import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../../services/invoiceService';
import { Calendar, Filter, Search, Plus, Send, CheckCircle, Trash2, Copy } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/ui/PermissionGate';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';

import Icon from '../../components/AppIcon';


export default function InvoiceListPage() {
  const { profile, userRoles, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleInvoices, setSampleInvoices] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    paymentStatus: 'all',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Real-time invoices synchronization
  const transformInvoice = (dbInvoice) => ({
    id: dbInvoice?.id,
    tenantId: dbInvoice?.tenant_id,
    invoiceNumber: dbInvoice?.invoice_number,
    customerId: dbInvoice?.customer_id,
    issueDate: dbInvoice?.issue_date,
    dueDate: dbInvoice?.due_date,
    status: dbInvoice?.status,
    subtotal: dbInvoice?.subtotal,
    taxAmount: dbInvoice?.tax_amount,
    totalAmount: dbInvoice?.total_amount,
    paidAmount: dbInvoice?.paid_amount,
    notes: dbInvoice?.notes,
    paymentTerms: dbInvoice?.payment_terms,
    createdAt: dbInvoice?.created_at,
    updatedAt: dbInvoice?.updated_at
  });

  const { data: realtimeInvoices, setData: setRealtimeInvoices, isConnected } = useRealtimeEntity(
    'invoices',
    invoices,
    transformInvoice
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setInvoices(realtimeInvoices);
    }
  }, [realtimeInvoices, isConnected]);

  useEffect(() => {
    loadInvoices();
  }, [profile?.tenant_id]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');

      // Super Admins see ALL invoices across ALL tenants
      // Business Admins and below see only their tenant's invoices
      const superAdmin = isSuperAdmin();
      const tenantId = superAdmin ? null : profile?.tenant_id;
      const data = await invoiceService?.getAll(tenantId, superAdmin);

      setInvoices(data);
    } catch (err) {
      setError(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = () => {
    navigate('/invoice-builder');
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/invoice-builder?id=${invoiceId}`);
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoice-builder?id=${invoiceId}&view=true`);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await invoiceService?.delete(invoiceId);
      await loadInvoices();
    } catch (err) {
      setError(err?.message || 'Failed to delete invoice');
    }
  };

  const handleDuplicateInvoice = async (invoice) => {
    navigate(`/invoice-builder?duplicate=${invoice?.id}`);
  };

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await invoiceService?.updatePaymentStatus(invoiceId, 'paid');
      await loadInvoices();
    } catch (err) {
      setError(err?.message || 'Failed to mark invoice as paid');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await invoiceService?.updateStatus(invoiceId, 'sent');
      await loadInvoices();
    } catch (err) {
      setError(err?.message || 'Failed to send invoice');
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      if (prev?.includes(invoiceId)) {
        return prev?.filter(id => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices?.length === invoices?.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices?.map(inv => inv?.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedInvoices?.length === 0) return;

    try {
      if (action === 'markPaid') {
        for (const id of selectedInvoices) {
          await invoiceService?.updatePaymentStatus(id, 'paid');
        }
      } else if (action === 'send') {
        for (const id of selectedInvoices) {
          await invoiceService?.updateStatus(id, 'sent');
        }
      } else if (action === 'delete') {
        if (!window.confirm(`Are you sure you want to delete ${selectedInvoices?.length} invoices?`)) return;
        for (const id of selectedInvoices) {
          await invoiceService?.delete(id);
        }
      }
      setSelectedInvoices([]);
      await loadInvoices();
    } catch (err) {
      setError(err?.message || 'Failed to perform bulk action');
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const superAdmin = isSuperAdmin();
        const tenantId = superAdmin ? null : profile?.tenant_id;
        const sampleData = await sampleDataService?.getSampleDataByEntity('invoices', tenantId);
        setSampleInvoices(sampleData);
      } else {
        setSampleInvoices([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      unpaid: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const filteredInvoices = invoices?.filter(invoice => {
    if (!filters?.search) return true;
    const searchLower = filters?.search?.toLowerCase();
    return (invoice?.invoiceNumber?.toLowerCase()?.includes(searchLower) ||
    invoice?.customer?.companyName?.toLowerCase()?.includes(searchLower) || invoice?.customer?.email?.toLowerCase()?.includes(searchLower));
  });

  // Combine real and sample data if sample data is active
  const displayInvoices = showSampleData ? [...invoices, ...sampleInvoices] : invoices;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 mt-1">Manage customer invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <SampleDataToggle
            entityType="invoices"
            onToggle={handleSampleDataToggle}
            isActive={showSampleData}
          />
          <PermissionGate feature="invoices" level="write">
            <Button onClick={() => navigate('/invoice-builder')}>
              <Icon name="Plus" size={16} className="mr-2" />
              Create Invoice
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by invoice number, customer name, or email..."
                value={filters?.search}
                onChange={(e) => setFilters({ ...filters, search: e?.target?.value })}
                className="pl-10"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter size={20} />
            Filters
          </Button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t">
            <Select
              value={filters?.status}
              onChange={(e) => setFilters({ ...filters, status: e?.target?.value })}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </Select>

            <Select
              value={filters?.paymentStatus}
              onChange={(e) => setFilters({ ...filters, paymentStatus: e?.target?.value })}
            >
              <option value="all">All Payment Status</option>
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </Select>

            <Input
              type="date"
              value={filters?.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e?.target?.value })}
              placeholder="Start Date"
            />

            <Input
              type="date"
              value={filters?.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e?.target?.value })}
              placeholder="End Date"
            />

            <div className="flex gap-2">
              <Input
                type="number"
                value={filters?.minAmount}
                onChange={(e) => setFilters({ ...filters, minAmount: e?.target?.value })}
                placeholder="Min Amount"
                className="w-1/2"
              />
              <Input
                type="number"
                value={filters?.maxAmount}
                onChange={(e) => setFilters({ ...filters, maxAmount: e?.target?.value })}
                placeholder="Max Amount"
                className="w-1/2"
              />
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(filters?.status !== 'all' || filters?.paymentStatus !== 'all' || filters?.startDate || filters?.endDate || filters?.minAmount || filters?.maxAmount) && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
            {filters?.status !== 'all' && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                Status: {filters?.status}
              </span>
            )}
            {filters?.paymentStatus !== 'all' && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                Payment: {filters?.paymentStatus}
              </span>
            )}
            {filters?.startDate && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                From: {formatDate(filters?.startDate)}
              </span>
            )}
            {filters?.endDate && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                To: {formatDate(filters?.endDate)}
              </span>
            )}
            <button
              onClick={() => setFilters({ status: 'all', paymentStatus: 'all', startDate: '', endDate: '', minAmount: '', maxAmount: '', search: '' })}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedInvoices?.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <span className="text-blue-800 font-medium">{selectedInvoices?.length} invoice(s) selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('send')}>
              <Send size={16} className="mr-1" />
              Send
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('markPaid')}>
              <CheckCircle size={16} className="mr-1" />
              Mark as Paid
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkAction('delete')} className="text-red-600 hover:text-red-700">
              <Trash2 size={16} className="mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedInvoices?.length === invoices?.length && invoices?.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayInvoices?.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Calendar size={48} className="text-gray-300 mb-3" />
                      <p className="text-lg font-medium text-gray-900 mb-1">No invoices found</p>
                      <p className="text-sm text-gray-500">Create your first invoice to get started</p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayInvoices?.map((invoice) => (
                  <tr key={invoice?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedInvoices?.includes(invoice?.id)}
                        onChange={() => handleSelectInvoice(invoice?.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewInvoice(invoice?.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {invoice?.invoiceNumber}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{invoice?.customer?.companyName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{invoice?.customer?.email || ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(invoice?.invoiceDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(invoice?.dueDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(invoice?.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice?.status)}`}>
                        {invoice?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(invoice?.paymentStatus)}`}>
                        {invoice?.paymentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewInvoice(invoice?.id)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEditInvoice(invoice?.id)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDuplicateInvoice(invoice)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Duplicate"
                        >
                          <Copy size={18} />
                        </button>
                        <button
                          onClick={() => handleSendInvoice(invoice?.id)}
                          className="text-green-600 hover:text-green-800"
                          title="Send"
                        >
                          <Send size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteInvoice(invoice?.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-sm text-gray-600 mb-1">Total Invoices</div>
          <div className="text-2xl font-bold text-gray-900">{invoices?.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-sm text-gray-600 mb-1">Total Amount</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(invoices?.reduce((sum, inv) => sum + (Number(inv?.totalAmount) || 0), 0))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-sm text-gray-600 mb-1">Paid</div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(invoices?.filter(inv => inv?.paymentStatus === 'paid')?.reduce((sum, inv) => sum + (Number(inv?.totalAmount) || 0), 0))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-sm text-gray-600 mb-1">Unpaid</div>
          <div className="text-2xl font-bold text-red-600">
            {formatCurrency(invoices?.filter(inv => inv?.paymentStatus === 'unpaid')?.reduce((sum, inv) => sum + (Number(inv?.totalAmount) || 0), 0))}
          </div>
        </div>
      </div>
    </div>
  );
}